import { create } from 'zustand'

/**
 * Theme store — toggle manual de tema light/dark.
 *
 * El valor inicial proviene del script anti-flash en index.html, que aplica
 * `.dark` al <html> antes del primer render leyendo localStorage o
 * `prefers-color-scheme`. Aquí solo sincronizamos el state con esa decisión.
 *
 * Persiste en localStorage bajo `hivanexus.theme` (valores: 'light' | 'dark').
 *
 * Ver Docs/18-BRAND.md §6.2.
 */

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'hivanexus.theme'

function readInitialTheme(): Theme {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    /* localStorage no disponible — silenciar */
  }
}

interface ThemeState {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggle: () => void
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: readInitialTheme(),
  setTheme: (theme) => {
    applyTheme(theme)
    set({ theme })
  },
  toggle: () => {
    const next: Theme = get().theme === 'light' ? 'dark' : 'light'
    applyTheme(next)
    set({ theme: next })
  },
}))
